import React, { useState } from 'react';
import { ParticipantView } from '@views';
import { Box, Button, Typography, Snackbar, Alert } from '@mui/material';
import { palette } from '@app/palette';
import logger from '@utils/logger';
import { CohortUserStore } from '@stores/CohortUserStore';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

interface CohortUserPaymentEditorProps {
  cohortUser: ParticipantView;
  cohortId: number;
  onSave?: (participantEmail: string) => void;
}

type PaymentData = {
  id: number;
  pledgeReceived: boolean;
  pledgeTransactionId: string;
};

export const CohortUserPaymentEditor: React.FC<CohortUserPaymentEditorProps> = ({ cohortUser, cohortId, onSave }) => {
  const cohortUserStore = new CohortUserStore();
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Validate cohortUser prop and extract payment data
  const [paymentData, setPaymentData] = useState<PaymentData>(() => {
    try {
      // Validate cohortUser prop structure
      if (!cohortUser) {
        const error = 'CohortUser prop is missing';
        logger.error(error, { cohortUser });
        setInitializationError(error);
        return { id: 0, pledgeReceived: false, pledgeTransactionId: '' };
      }

      if (!cohortUser.CohortUser) {
        const error = 'CohortUser.CohortUser object is missing';
        logger.error(error, { cohortUser });
        setInitializationError(error);
        return { id: 0, pledgeReceived: false, pledgeTransactionId: '' };
      }

      if (!cohortUser.CohortUser.id) {
        const error = 'CohortUser ID is missing';
        logger.error(error, { cohortUser: cohortUser.CohortUser });
        setInitializationError(error);
        return { id: 0, pledgeReceived: false, pledgeTransactionId: '' };
      }

      // Extract payment data safely with defaults
      return {
        id: cohortUser.CohortUser.id,
        pledgeReceived: cohortUser.CohortUser.pledgeReceived ?? false,
        pledgeTransactionId: cohortUser.CohortUser.pledgeTransactionId ?? '',
      };
    } catch (err) {
      const error = 'Unexpected error during component initialization';
      logger.error(error, {
        error: err,
        cohortUser: cohortUser,
      });
      setInitializationError(error);
      return { id: 0, pledgeReceived: false, pledgeTransactionId: '' };
    }
  });

  // Create rows array with single payment data object
  const rows = [paymentData];

  const processRowUpdate = (newRow: PaymentData) => {
    try {
      // Validate the new row data
      if (!newRow) {
        logger.error('Invalid row data in processRowUpdate', { newRow });
        showSnackbar('Invalid data entered. Please try again.');
        return paymentData; // Return current data to prevent corruption
      }

      // Validate required fields exist
      if (typeof newRow.pledgeReceived !== 'boolean') {
        logger.error('Invalid pledgeReceived value in processRowUpdate', {
          newRow,
          pledgeReceived: newRow.pledgeReceived,
          type: typeof newRow.pledgeReceived,
        });
        showSnackbar('Invalid pledge received value. Please enter true or false.');
        return paymentData; // Return current data to prevent corruption
      }

      // pledgeTransactionId can be any string including empty string, so just ensure it's a string
      if (typeof newRow.pledgeTransactionId !== 'string') {
        logger.error('Invalid pledgeTransactionId value in processRowUpdate', {
          newRow,
          pledgeTransactionId: newRow.pledgeTransactionId,
          type: typeof newRow.pledgeTransactionId,
        });
        showSnackbar('Invalid transaction ID value. Please enter a valid text value.');
        return paymentData; // Return current data to prevent corruption
      }

      // Update local state immediately when cells are edited
      setPaymentData(newRow);
      return newRow;
    } catch (err) {
      logger.error('Unexpected error in processRowUpdate', {
        error: err,
        newRow: newRow,
        currentPaymentData: paymentData,
      });
      showSnackbar('An unexpected error occurred while updating the data. Please try again.');
      return paymentData; // Return current data to prevent corruption
    }
  };

  const handleSaveRow = async () => {
    // Validate cohortUser object exists and has required properties
    if (!cohortUser) {
      logger.error('CohortUser prop is missing', { cohortUser });
      showSnackbar('Failed to save payment data: Invalid cohort user data');
      return;
    }

    if (!cohortUser.CohortUser) {
      logger.error('CohortUser.CohortUser object is missing', {
        cohortUser: cohortUser,
      });
      showSnackbar('Failed to save payment data: Invalid cohort user structure');
      return;
    }

    if (!cohortUser.CohortUser.id) {
      logger.error('CohortUser ID is missing', {
        cohortUser: cohortUser.CohortUser,
      });
      showSnackbar('Failed to save payment data: Missing user identifier');
      return;
    }

    // Validate cohortId is available
    if (cohortId === null) {
      logger.error('CohortId not available for save operation', {
        cohortUserId: cohortUser.CohortUser.id,
        cohortId: cohortId,
      });
      showSnackbar('Failed to save payment data: Cohort information not loaded. Please refresh and try again.');
      return;
    }

    // Validate payment data exists
    if (!paymentData) {
      logger.error('Payment data is missing', {
        cohortUserId: cohortUser.CohortUser.id,
        paymentData: paymentData,
      });
      showSnackbar('Failed to save payment data: No payment information available');
      return;
    }

    try {
      // Create updated CohortUser object with new payment data
      const updatedCohortUser = {
        ...cohortUser.CohortUser,
        pledgeReceived: paymentData.pledgeReceived,
        pledgeTransactionId: paymentData.pledgeTransactionId,
      };

      // Persist changes to backend using CohortUserStore.update()
      const response = await cohortUserStore.update(updatedCohortUser, cohortId);

      if (response.status === 'SUCCESS') {
        // Display success alert
        showSnackbar('Payment data saved successfully', 'success');
        logger.info('Payment data saved successfully', {
          cohortUserId: cohortUser.CohortUser.id,
          pledgeReceived: paymentData.pledgeReceived,
          pledgeTransactionId: paymentData.pledgeTransactionId,
        });

        // Call the onSave callback to refresh parent data
        if (onSave) {
          onSave(cohortUser.email);
        }
      } else {
        // Log API response error with details
        logger.error('Failed to save payment data - API response error', {
          cohortUserId: cohortUser.CohortUser.id,
          responseStatus: response.status,
          error: response.error,
          attemptedData: {
            pledgeReceived: paymentData.pledgeReceived,
            pledgeTransactionId: paymentData.pledgeTransactionId,
          },
        });
        showSnackbar('Failed to save payment data: Server error occurred. Please try again.');
      }
    } catch (err) {
      // Log error with comprehensive context
      logger.error('Failed to save payment data - Network or unexpected error', {
        cohortUserId: cohortUser.CohortUser.id,
        cohortId: cohortId,
        error: err,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        errorStack: err instanceof Error ? err.stack : undefined,
        attemptedData: {
          pledgeReceived: paymentData.pledgeReceived,
          pledgeTransactionId: paymentData.pledgeTransactionId,
        },
      });
      // Display user-friendly error message
      showSnackbar('Failed to save payment data: Network error occurred. Please check your connection and try again.');
    }
  };

  const columns: GridColDef<PaymentData>[] = [
    {
      field: 'pledgeReceived',
      headerName: 'Pledge Received',
      type: 'boolean',
      editable: true,
      width: 150,
    },
    {
      field: 'pledgeTransactionId',
      headerName: 'Transaction Notes',
      type: 'string',
      editable: true,
      flex: 1,
    },
    {
      field: '__actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: () => (
        <Button size="small" variant="contained" onClick={handleSaveRow}>
          Save
        </Button>
      ),
    },
  ];

  // Show error state if initialization failed
  if (initializationError) {
    return (
      <Box
        borderRadius={8}
        border={1}
        borderColor={palette.background.border}
        display="block"
        position="relative"
        padding={4}
        margin={4}
      >
        <Typography
          variant="subtitle1"
          sx={{
            backgroundColor: palette.background.paper,
            position: 'absolute',
            top: -32,
            left: 48,
            padding: 2,
          }}
        >
          Payment Information
        </Typography>
        <Typography variant="body1" color="error" sx={{ textAlign: 'center', padding: 4 }}>
          Error loading payment editor: {initializationError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      borderRadius={8}
      border={1}
      borderColor={palette.background.border}
      display="block"
      position="relative"
      padding={4}
      margin={4}
    >
      <Typography
        variant="subtitle1"
        sx={{
          backgroundColor: palette.background.paper,
          position: 'absolute',
          top: -32,
          left: 48,
          padding: 2,
        }}
      >
        Payment Information
      </Typography>

      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        editMode="cell"
        processRowUpdate={processRowUpdate}
        hideFooter
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: 'Roboto, serif',
            letterSpacing: '0.5px',
          },
          '& .MuiDataGrid-cell': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
          '& .MuiDataGrid-cell--editing': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
          '& .MuiDataGrid-cell--editing input': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
          '& .MuiInputBase-input': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontFamily: 'Roboto, serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
